import { nth } from "@util/dates"
import React from "react"
import "react-datepicker/dist/react-datepicker.css"
import { Pencil, Trash, X, GripVertical } from "react-bootstrap-icons"
import { DraggableProvidedDragHandleProps } from "@hello-pangea/dnd"
import { EventPerformanceType } from "../../types/events"

interface EventPerformanceProps {
  performance: EventPerformanceType
  index: number
  onRemove: (performanceOrder: number) => void
  onEdit?: (performance: EventPerformanceType) => void
  isEditing?: boolean
  isAnyEditing?: boolean
  dragHandleProps?: DraggableProvidedDragHandleProps | null
  isDraggable?: boolean
}

export const EventPerformance: React.FC<EventPerformanceProps> = ({
  performance,
  index,
  onRemove,
  onEdit,
  isEditing = false,
  isAnyEditing = false,
  dragHandleProps = null,
  isDraggable = false,
}) => {
  return (
    <div
      className={`mb-2 transition-all duration-200 ${
        isEditing
          ? ""
          : isAnyEditing
            ? "opacity-25 p-2"
            : "hover:bg-gray-50 rounded-lg py-2"
      }`}
      data-performance-order={performance.performance_order}
    >
      <div className="grid grid-cols-12 gap-2 items-center">
        {/* Drag handle or performance number */}
        <div className="col-span-1 text-sm text-gray-400 text-left">
          {isDraggable && !isAnyEditing ? (
            <span
              {...dragHandleProps}
              className="cursor-grab active:cursor-grabbing text-gray-600 hover:text-gray-500"
              title="Drag to reorder"
            >
              <GripVertical className="w-4 h-4" />
            </span>
          ) : (
            <>
              {index + 1}
              {nth(index + 1)}
            </>
          )}
        </div>

        {/* Set Time */}
        <div className="col-span-2 text-sm text-gray-700 text-center">
          {performance.set_time ? (
            (() => {
              let timeString: string
              if (performance.set_time instanceof Date) {
                timeString = performance.set_time.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })
              } else if (typeof performance.set_time === "string") {
                try {
                  const date = new Date(performance.set_time)
                  if (!isNaN(date.getTime())) {
                    timeString = date.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  } else {
                    timeString = performance.set_time
                  }
                } catch (e) {
                  timeString = performance.set_time
                }
              } else {
                timeString = "Invalid time"
              }
              return timeString
            })()
          ) : (
            <span className="text-gray-400 italic">No time set</span>
          )}
        </div>

        {/* Band Name */}
        <div
          className={`col-span-3 min-w-0 truncate text-gray-900 ${
            performance.band_id ? "font-bold" : ""
          }`}
        >
          {performance.band_name}
        </div>

        {/* Band Email */}
        <div className="col-span-3 min-w-0 text-xs text-gray-500 truncate">
          {performance.pending_band_email}
        </div>

        {/* Action Buttons */}
        <div className="col-span-3 min-w-0 text-center flex justify-center space-x-1">
          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit(performance)}
              disabled={isAnyEditing && !isEditing}
              className={`p-1 rounded transition-colors focus:outline-none ${
                isAnyEditing && !isEditing
                  ? "text-gray-400 cursor-not-allowed"
                  : isEditing
                    ? "text-orange-600 hover:text-orange-800 hover:bg-orange-50"
                    : "text-blue-600 hover:text-blue-800 hover:bg-blue-50"
              }`}
              title={
                isAnyEditing && !isEditing
                  ? "Finish editing current performance first"
                  : isEditing
                    ? "Cancel editing"
                    : "Edit performance"
              }
            >
              {isEditing ? (
                <X className="w-4 h-4" />
              ) : (
                <Pencil className="w-4 h-4" />
              )}
            </button>
          )}
          {!isEditing && (
            <button
              type="button"
              onClick={() => onRemove(performance.performance_order)}
              disabled={isAnyEditing && !isEditing}
              className={`p-1 rounded transition-colors focus:outline-none ${
                isAnyEditing && !isEditing
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-red-600 hover:text-red-800 hover:bg-red-50"
              }`}
              title={
                isAnyEditing && !isEditing
                  ? "Finish editing current performance first"
                  : "Remove performance"
              }
            >
              <Trash className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
